
import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams, Navigate, Outlet } from 'react-router-dom';
import { DataProvider } from './context/DataContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LocaleProvider, useLocale } from './context/LocaleContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import FloatingContact from './components/FloatingContact';
import AppShellLoader from './components/AppShellLoader';
import AppErrorBoundary from './components/AppErrorBoundary';
import { appRoutes } from './lib/routes';

const Home = lazy(() => import('./pages/Home'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const ProductRouteResolver = lazy(() => import('./pages/ProductRouteResolver'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Contact = lazy(() => import('./pages/Contact'));
const News = lazy(() => import('./pages/News'));
const NewsDetail = lazy(() => import('./pages/NewsDetail'));
const Gallery = lazy(() => import('./pages/Gallery'));
const CommercialTool = lazy(() => import('./pages/CommercialTool'));
const Login = lazy(() => import('./pages/Login'));
const AdminInteractiveMapContent = lazy(() => import('./pages/Admin/InteractiveMapContent'));
const AdminNews = lazy(() => import('./pages/Admin/News'));
const AdminNewsEditor = lazy(() => import('./pages/Admin/NewsEditor'));
const AdminUsers = lazy(() => import('./pages/Admin/Users'));
const NotFound = lazy(() => import('./pages/NotFound'));

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();
  const { locale } = useLocale();
  const location = useLocation();

  if (isLoading) {
    return <AppShellLoader label={locale === 'zh' ? '正在检查安全会话...' : 'Checking secure session...'} compact />;
  }

  if (!isAuthenticated) {
    const redirectPath = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`${appRoutes.login}?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  if (!isAdmin) {
    const redirectPath = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`${appRoutes.login}?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  return <>{children}</>;
};

const PublicLayout: React.FC = () => {
  const { locale } = useLocale();

  return (
    <>
        <Navbar />
        <main className="grow">
        <Suspense fallback={<AppShellLoader label={locale === 'zh' ? '正在加载页面...' : 'Loading page...'} compact />}>
          <Outlet />
        </Suspense>
      </main>
      <FloatingContact />
      <Footer />
    </>
  );
};

const InteractiveMapLayout: React.FC = () => {
  const { locale } = useLocale();

  return (
    <Suspense fallback={<AppShellLoader label={locale === 'zh' ? '正在加载互动地图...' : 'Loading interactive map...'} compact />}>
      <Outlet />
    </Suspense>
  );
};

const LocalizedAppBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { locale } = useLocale();
  return <AppErrorBoundary locale={locale}>{children}</AppErrorBoundary>;
};

const LegacyProductCategoryRedirect: React.FC = () => {
  const { category } = useParams<{ category?: string }>();
  const location = useLocation();

  if (!category) {
    return <Navigate to={appRoutes.products} replace />;
  }

  return <Navigate to={`${appRoutes.products}/${category}${location.search}`} replace />;
};

const LegacyProductsRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={`${appRoutes.products}${location.search}`} replace />;
};

const AppRoutes: React.FC = () => {
  const { locale } = useLocale();

  return (
    <LocalizedAppBoundary>
      <AuthProvider>
        <DataProvider>
          <Router>
            <ScrollToTop />
            <div className="min-h-screen flex flex-col font-sans antialiased text-gray-900 bg-white">
              <Routes>
                <Route
                  path={appRoutes.login}
                  element={
                    <Suspense fallback={<AppShellLoader label={locale === 'zh' ? '正在加载登录页面...' : 'Loading sign-in...'} compact />}>
                      <Login />
                    </Suspense>
                  }
                />
                <Route
                  path={appRoutes.admin}
                  element={<Navigate to={appRoutes.adminNews} replace />}
                />
                <Route
                  path={appRoutes.adminMapContent}
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<AppShellLoader label={locale === 'zh' ? '正在加载地图内容...' : 'Loading map content...'} compact />}>
                        <AdminInteractiveMapContent />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={appRoutes.adminNews}
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<AppShellLoader label={locale === 'zh' ? '正在加载文章列表...' : 'Loading posts...'} compact />}>
                        <AdminNews />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={appRoutes.adminNewsNew}
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<AppShellLoader label={locale === 'zh' ? '正在加载编辑器...' : 'Loading editor...'} compact />}>
                        <AdminNewsEditor />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={`${appRoutes.adminNews}/:id/edit`}
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<AppShellLoader label={locale === 'zh' ? '正在加载编辑器...' : 'Loading editor...'} compact />}>
                        <AdminNewsEditor />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path={appRoutes.adminUsers}
                  element={
                    <ProtectedRoute>
                      <Suspense fallback={<AppShellLoader label={locale === 'zh' ? '正在加载账户...' : 'Loading accounts...'} compact />}>
                        <AdminUsers />
                      </Suspense>
                    </ProtectedRoute>
                  }
                />

                <Route element={<InteractiveMapLayout />}>
                  <Route path={appRoutes.commercialTool} element={<CommercialTool />} />
                  <Route path="/interactivemap" element={<Navigate to={appRoutes.commercialTool} replace />} />
                  <Route path={appRoutes.legacyCommercialTool} element={<Navigate to={appRoutes.commercialTool} replace />} />
                </Route>

                <Route element={<PublicLayout />}>
                  <Route path={appRoutes.home} element={<Home />} />
                  <Route path={appRoutes.products} element={<Products />} />
                  <Route path={`${appRoutes.productBase}/:id`} element={<ProductDetail />} />
                  <Route path={`${appRoutes.products}/:slug`} element={<ProductRouteResolver />} />
                  <Route path={appRoutes.legacyProducts} element={<LegacyProductsRedirect />} />
                  <Route path={`${appRoutes.legacyProducts}/:category`} element={<LegacyProductCategoryRedirect />} />
                  <Route path={appRoutes.about} element={<AboutUs />} />
                  <Route path={appRoutes.news} element={<News />} />
                  <Route path={`${appRoutes.news}/:slug`} element={<NewsDetail />} />
                  <Route path={`${appRoutes.news}/:legacyId/:legacySlug`} element={<NewsDetail />} />
                  <Route path={appRoutes.gallery} element={<Gallery />} />
                  <Route path={appRoutes.contact} element={<Contact />} />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </div>
          </Router>
        </DataProvider>
      </AuthProvider>
    </LocalizedAppBoundary>
  );
};

const App: React.FC = () => {
  return (
    <LocaleProvider>
      <AppRoutes />
    </LocaleProvider>
  );
};

export default App;
